import {useSignal, useSignals} from '@preact/signals-react/runtime'
import {useQueryClient} from '@tanstack/react-query'
import {Button} from 'primereact/button'
import {Dialog} from 'primereact/dialog'
import {Dropdown} from 'primereact/dropdown'
import {
    forwardRef,
    useCallback,
    useImperativeHandle,
    useMemo,
    useRef,
} from 'react'
import {useParams} from 'react-router-dom'
import TranslateUtils from '../../i18n/TranslateUtils'
import {
    QueryCRUDTypes,
    QueryKeyManager,
} from '../../query-manager/QueryKeysManager'
import {QueryKeyTypes} from '../../query-manager/QueryKeyTypes'
import {
    blockOverflownScroll,
    unblockOverflownScroll,
} from '../../utils/BlockScrollsUtils'
import {useAuth} from '../authentication/AuthProvider'
import SliderUtils from '../form-utils/SliderUtils'
import {useBiometricCRUD} from './query-keys/BiometricsQueryManager'

const BiometricsForm = forwardRef((props, ref) => {
    useSignals()
    useImperativeHandle(ref, () => ({
        showForm: handleShowForm,
        hideForm: handleHideForm,
    }))

    //#region Translate
    const {translateKeys, translateCommons} = TranslateUtils()
    //#endregion

    //#region Context
    const {user} = useAuth()
    //#endregion

    //#region ID
    const {id: idString} = useParams()
    const patientId = useMemo(() => {
        if (!idString) {
            return null
        }

        return parseInt(idString)
    }, [idString])
    //#endregion

    //#region State
    const selected = useSignal()
    const visible = useSignal(false)
    const data = useSignal({})
    //#endregion

    //#region Ref

    const heightRef = useRef(null)
    const weightRef = useRef(null)
    //#endregion

    //#region Query
    const queryClient = useQueryClient()

    const {mutateAsync: createOrUpdateBiometric} = useBiometricCRUD(
        !!data.value?.id ? QueryCRUDTypes.update : QueryCRUDTypes.create,
        user?.id,
    )
    //#endregion

    //#region Handle Function
    const handleShowForm = value => {
        if (!!value) {
            data.value = {
                height: value && value?.height ? value?.height : 0,
                weight: value && value?.weight ? value?.weight : 0,
                type: value && value?.type ? value?.type : 'cmKg',
            }

            selected.value = value && value?.type ? value?.type : 'cmKg'
        }

        blockOverflownScroll()
        visible.value = true
    }

    const handleHideForm = () => {
        unblockOverflownScroll()
        data.value = {}
        visible.value = false
    }

    const handleOnSubmit = async () => {
        let toPostData = {
            ...data.value,
            type: selected.value,
            id: data.value?.idx,
            userId: patientId,
        }

        const biometricsData = await createOrUpdateBiometric(toPostData)

        if (biometricsData?.id) {
            const {biometricsKeys} = QueryKeyManager(
                QueryKeyTypes.biometrics,
                user?.id,
            )

            const {patientKeys} = QueryKeyManager(
                QueryKeyTypes.patient,
                user?.id,
            )

            //TODO: Temporary Refetch Table
            queryClient.invalidateQueries({
                queryKey: biometricsKeys.pattern(),
            })

            //TODO: Temporary Refetch Table
            queryClient.invalidateQueries({
                queryKey: patientKeys.detail({id: patientId}),
            })

            handleHideForm()
        }
    }

    const handleOnChange = useCallback(
        (key, value) => {
            data.value = {...data.value, [key]: value}
        },
        [data],
    )

    const handleOnChangeType = useCallback(
        e => {
            const cm = 2.54
            const kg = 2.20462262185
            if (data.value && data.value?.type) {
                selected.value = e.value

                if (data.value?.height && data.value?.weight) {
                    if (
                        e.value === 'cmKg' &&
                        data.value?.type === 'inchPound'
                    ) {
                        let height = Math.round(data.value?.height * cm)
                        let weight = Math.round(data.value?.weight / kg)

                        data.value = {
                            type: e.value,
                            height: height,
                            weight: weight,
                        }

                        heightRef.current.onChange(height)
                        weightRef.current.onChange(weight)
                    } else if (
                        e.value === 'inchPound' &&
                        data.value?.type === 'cmKg'
                    ) {
                        let height = Math.round(data.value?.height / cm)
                        let weight = Math.round(data.value?.weight * kg)

                        data.value = {
                            type: e.value,
                            height: height,
                            weight: weight,
                        }

                        heightRef.current.onChange(height)
                        weightRef.current.onChange(weight)
                    }
                }
            }
        },
        [data, selected],
    )
    //#endregion

    return (
        <div>
            <Dialog
                header={
                    <h5 className=" mb-0 font-bold">
                        <strong>{translateKeys('BMI Form')}</strong>
                    </h5>
                }
                style={{width: '40vw'}}
                breakpoints={{'1440px': '75vw', '800px': '100vw'}}
                visible={visible.value}
                onHide={handleHideForm}>
                <div className="flex flex-wrap col-12">
                    <div className="col-12">
                        <h5 className="font-bold">{translateKeys('Type')}</h5>
                        <Dropdown
                            className="col-12 h-3rem p-0"
                            value={selected.value}
                            defaultValue={'inchPound'}
                            onChange={e => handleOnChangeType(e)}
                            options={[
                                {
                                    label: translateKeys('Inch/Pound'),
                                    value: 'inchPound',
                                },
                                {
                                    label: translateKeys('Cm/Kg'),
                                    value: 'cmKg',
                                },
                            ]}
                        />
                    </div>
                    <div className="col-12 sm:col-12 md:col-6 lg:col-6 ">
                        {' '}
                        <h5 className="font-bold">{translateKeys('Height')}</h5>
                        <div className="flex w-full">
                            <div className="flex-grow-1">
                                <SliderUtils
                                    min={0}
                                    max={
                                        selected.value === 'inchPound'
                                            ? 115
                                            : 300
                                    }
                                    step={0.1}
                                    ref={heightRef}
                                    state={true}
                                    maxFractionDigits={1}
                                    minFractionDigits={1}
                                    defaultValue={
                                        data.value && data.value?.height
                                            ? data.value?.height
                                            : 0
                                    }
                                    borderNoRoundLeft
                                    onChangeValue={value =>
                                        handleOnChange('height', value)
                                    }
                                    maxLength={5}
                                />
                            </div>
                            <div className="flex-none">
                                {' '}
                                <Button
                                    className="my-2 pb-3 border-noround-left"
                                    label={translateKeys(
                                        selected.value === 'inchPound'
                                            ? 'in'
                                            : 'Cm',
                                    )}
                                />
                            </div>
                        </div>
                    </div>
                    <div className="col-12 sm:col-12 md:col-6 lg:col-6 ">
                        {' '}
                        <h5 className="font-bold">{translateKeys('Weight')}</h5>
                        <div className="flex w-full">
                            <div className="flex-grow-1">
                                <SliderUtils
                                    min={0}
                                    max={
                                        selected.value === 'inchPound'
                                            ? 661
                                            : 300
                                    }
                                    step={0.1}
                                    ref={weightRef}
                                    state={true}
                                    maxFractionDigits={1}
                                    minFractionDigits={1}
                                    defaultValue={
                                        data.value && data.value?.weight
                                            ? data.value?.weight
                                            : 0
                                    }
                                    borderNoRoundLeft
                                    onChangeValue={value => {
                                        handleOnChange('weight', value)
                                    }}
                                    maxLength={5}
                                />
                            </div>
                            <div className="flex-none">
                                {' '}
                                <Button
                                    className="my-2 pb-3 border-noround-left"
                                    label={translateKeys(
                                        selected.value === 'inchPound'
                                            ? 'lbs'
                                            : 'Kg',
                                    )}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="col-12 py-3">
                        <Button
                            label={translateCommons('SUBMIT')}
                            onClick={handleOnSubmit}
                            icon={'pi pi-send'}
                            className="col-12 p-button-lg font-bold"
                        />
                    </div>
                </div>
            </Dialog>
        </div>
    )
})

export default BiometricsForm
